// ═══════════════════════════════════════
// ATLAS BEASTS — SEASON PASS PUBLIC PAGE
// Public marketing page for season pass, events, and news
// ═══════════════════════════════════════

import { useNavigate } from 'react-router-dom'

const SEASON_PASS_TIERS = [
  {
    name: 'Basic Pass',
    price: 'Free',
    color: '#8A8AA5',
    features: [
      'Free tier rewards at levels 1, 10, 20, 30, 40, 50',
      'Season XP and progression',
      'Access to season events',
      'Basic creature drops',
    ],
  },
  {
    name: 'Deluxe Pass',
    price: '1000 💎',
    color: '#FFE800',
    features: [
      'All Basic Pass rewards',
      'Premium rewards at every tier',
      'Exclusive legendary creatures',
      '20% bonus Season XP',
      'Early access to new features',
      'Special seasonal cosmetics',
      'Priority customer support',
    ],
  },
]

const WEEKLY_EVENTS = [
  { 
    id: 1, 
    title: 'Legendary Creature Hunt', 
    date: 'Week 1 (May 8-14)', 
    desc: 'Hunt down legendary Fire creatures with increased spawn rates',
    type: 'event',
    element: 'fire',
  },
  { 
    id: 2, 
    title: 'Water Mastery Challenge', 
    date: 'Week 1 (May 8-14)', 
    desc: 'Complete battles using only Water creatures for bonus XP',
    type: 'challenge',
    element: 'water',
  },
  { 
    id: 3, 
    title: 'Earth Resource Rush', 
    date: 'Week 2 (May 15-21)', 
    desc: 'Double resource drops in Earth zones',
    type: 'event',
    element: 'earth',
  },
  { 
    id: 4, 
    title: 'Air Agility Tournament', 
    date: 'Week 2 (May 15-21)', 
    desc: 'PVP tournament with Air creature bonuses',
    type: 'tournament',
    element: 'air',
  },
  { 
    id: 5, 
    title: 'Void Convergence', 
    date: 'Week 3 (May 22-28)', 
    desc: 'Special Void-themed events with exclusive rewards',
    type: 'event',
    element: 'void',
  },
  { 
    id: 6, 
    title: 'Aether Ascension', 
    date: 'Week 4 (May 29-Jun 4)', 
    desc: 'Final week mega event with Aether creature drops',
    type: 'event',
    element: 'aether',
  },
]

const SEASON_NEWS = [
  {
    id: 1,
    date: 'May 8, 2026',
    title: 'Season 1 Now Live!',
    desc: 'The first season of Atlas Beasts has begun. Unlock exclusive rewards, participate in weekly events, and climb the leaderboards.',
    tag: 'announcement',
  },
  {
    id: 2,
    date: 'May 7, 2026',
    title: 'New Creatures Added',
    desc: '15 new creatures have been added to the game, including 5 legendary Fire and Water beasts.',
    tag: 'update',
  },
  {
    id: 3,
    date: 'May 5, 2026',
    title: 'Marketplace Overhaul',
    desc: 'The marketplace has been redesigned with a new grid layout and improved rarity indicators.',
    tag: 'update',
  },
  {
    id: 4,
    date: 'May 1, 2026',
    title: 'Balance Changes',
    desc: 'Several creatures have been rebalanced for better gameplay. Check the patch notes for details.',
    tag: 'patch',
  },
]

const ELEMENT_COLORS: Record<string, string> = {
  fire: '#FF4800',
  water: '#00F0FF',
  earth: '#39FF14',
  air: '#B8EEFF',
  void: '#BF00FF',
  aether: '#FFE800',
}

const EVENT_TYPE_COLORS: Record<string, string> = {
  event: '#00F0FF',
  challenge: '#39FF14',
  tournament: '#FFE800',
}

export default function SeasonPublic() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#020510' }}>
      
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-4" style={{ 
        background: 'rgba(2,5,16,0.8)', 
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(58,58,90,0.3)',
      }}>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg" style={{ 
            background: 'linear-gradient(135deg, #00F0FF, #BF00FF)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <span className="font-black text-sm" style={{ color: '#020510' }}>A</span>
          </div>
          <span className="font-bold text-lg uppercase" style={{ color: '#EAF6FF', fontFamily: 'Rajdhani, system-ui' }}>
            Atlas Beasts
          </span>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/landing')}
            className="px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wider transition-all hover:scale-105"
            style={{
              background: 'rgba(58,58,90,0.3)',
              color: '#EAF6FF',
              fontFamily: 'Rajdhani, system-ui',
              border: '1px solid rgba(58,58,90,0.5)',
            }}
          >
            Home
          </button>
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wider transition-all hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #00F0FF 0%, #00F0FF80 100%)',
              color: '#020510',
              fontFamily: 'Rajdhani, system-ui',
            }}
          >
            Play Now
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-20">
        {/* Animated background */}
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(255,232,0,0.08) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(191,0,255,0.06) 0%, transparent 50%)',
        }} />
        
        {/* Animated particles/dots */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} />

        <div className="relative z-10 px-4 max-w-5xl mx-auto w-full text-center">
          {/* Season badge */}
          <div className="mb-6" style={{ animation: 'fadeInUp 1s ease-out' }}>
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest" style={{
              color: '#FFE800',
              background: 'rgba(255,232,0,0.1)',
              border: '1px solid rgba(255,232,0,0.3)',
              fontFamily: 'Space Mono, monospace',
            }}>
              Season 1 — Fire & Fury
            </span>
          </div>

          {/* Main Title */}
          <h1 className="font-black text-6xl md:text-8xl uppercase tracking-tight mb-4" style={{ 
            color: '#EAF6FF',
            fontFamily: 'Rajdhani, system-ui',
            textShadow: '0 0 80px rgba(255,232,0,0.4), 0 0 160px rgba(191,0,255,0.2)',
            animation: 'fadeInUp 1s ease-out 0.1s both',
            lineHeight: 0.9,
          }}>
            BATTLE
            <br />
            <span style={{ 
              background: 'linear-gradient(135deg, #FFE800 0%, #FF4800 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>PASS</span>
          </h1>
          
          {/* Subtitle */}
          <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto" style={{ 
            color: '#8A8AA5',
            fontFamily: 'Inter, sans-serif',
            animation: 'fadeInUp 1s ease-out 0.3s both',
          }}>
            Unlock exclusive rewards, participate in weekly events, and climb the leaderboards in Season 1: Fire & Fury.
          </p>

          {/* Season timer */}
          <div className="flex justify-center gap-8 mb-8" style={{ animation: 'fadeInUp 1s ease-out 0.5s both' }}>
            {[
              { value: '30', label: 'Days' },
              { value: '04', label: 'Hours' },
              { value: '12', label: 'Minutes' },
              { value: '45', label: 'Seconds' },
            ].map((timer, i) => (
              <div key={i} className="text-center">
                <p className="font-black text-4xl md:text-5xl" style={{ 
                  color: '#EAF6FF', 
                  fontFamily: 'Rajdhani, system-ui',
                  textShadow: '0 0 20px rgba(255,232,0,0.3)',
                }}>
                  {timer.value}
                </p>
                <p className="text-xs uppercase tracking-wider" style={{ color: '#5A5A7A', fontFamily: 'Space Mono, monospace' }}>
                  {timer.label}
                </p>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <button
            onClick={() => navigate('/login')}
            className="px-12 py-5 rounded-xl font-black text-xl uppercase tracking-wider transition-all hover:scale-105 active:scale-95 relative overflow-hidden group mx-auto"
            style={{
              background: 'linear-gradient(135deg, #FFE800 0%, #FF4800 100%)',
              color: '#020510',
              fontFamily: 'Rajdhani, system-ui',
              boxShadow: '0 0 40px rgba(255,232,0,0.4), 0 0 80px rgba(255,72,0,0.2)',
              animation: 'fadeInUp 1s ease-out 0.7s both',
            }}
          >
            <span className="relative z-10">Get Battle Pass</span>
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 100%)',
            }} />
          </button>
        </div>
      </section>

      {/* Season Pass Tiers */}
      <section className="py-20 px-4" style={{ background: 'rgba(13,13,21,0.5)' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="font-black text-4xl md:text-5xl uppercase text-center mb-4" style={{ 
            color: '#EAF6FF',
            fontFamily: 'Rajdhani, system-ui',
          }}>
            PASS TIERS
          </h2>
          <p className="text-center mb-12" style={{ color: '#5A5A7A', fontFamily: 'Inter, sans-serif' }}>
            Choose your path to glory
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {SEASON_PASS_TIERS.map((tier, i) => (
              <div key={i} className="p-8 rounded-2xl border transition-all hover:scale-105" style={{ 
                background: 'rgba(13,13,21,0.8)',
                borderColor: tier.name === 'Deluxe Pass' ? `${tier.color}50` : 'rgba(58,58,90,0.5)',
                backdropFilter: 'blur(8px)',
                boxShadow: tier.name === 'Deluxe Pass' ? `0 0 30px ${tier.color}20` : 'none',
              }}>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-black text-2xl" style={{ color: '#EAF6FF', fontFamily: 'Rajdhani, system-ui' }}>
                    {tier.name}
                  </h3>
                  <span className="font-black text-2xl" style={{ 
                    color: tier.color,
                    fontFamily: 'Rajdhani, system-ui',
                  }}>
                    {tier.price}
                  </span>
                </div>
                <ul className="space-y-3">
                  {tier.features.map((feature, j) => (
                    <li key={j} className="flex items-start gap-3" style={{ color: '#EAF6FF', fontFamily: 'Inter, sans-serif' }}>
                      <span className="mt-1" style={{ color: tier.color }}>✓</span>
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full mt-6 py-3 rounded-xl font-bold uppercase tracking-wider transition-all hover:scale-105"
                  style={{
                    background: tier.name === 'Deluxe Pass' 
                      ? `linear-gradient(135deg, ${tier.color} 0%, ${tier.color}80 100%)`
                      : 'rgba(58,58,90,0.5)',
                    color: tier.name === 'Deluxe Pass' ? '#020510' : '#EAF6FF',
                    fontFamily: 'Rajdhani, system-ui',
                    border: tier.name === 'Deluxe Pass' ? 'none' : '1px solid rgba(58,58,90,0.5)',
                  }}
                >
                  {tier.name === 'Deluxe Pass' ? 'Get Deluxe' : 'Start Free'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Weekly Events */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-black text-4xl md:text-5xl uppercase text-center mb-4" style={{ 
            color: '#EAF6FF',
            fontFamily: 'Rajdhani, system-ui',
          }}>
            WEEKLY EVENTS
          </h2>
          <p className="text-center mb-12" style={{ color: '#5A5A7A', fontFamily: 'Inter, sans-serif' }}>
            Season 1 event calendar
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WEEKLY_EVENTS.map((event) => (
              <div key={event.id} className="p-6 rounded-2xl border transition-all hover:scale-105" style={{ 
                background: 'rgba(13,13,21,0.8)',
                borderColor: `${ELEMENT_COLORS[event.element]}40`,
                backdropFilter: 'blur(8px)',
              }}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-full" style={{ 
                    color: EVENT_TYPE_COLORS[event.type],
                    background: `${EVENT_TYPE_COLORS[event.type]}20`,
                    border: `1px solid ${EVENT_TYPE_COLORS[event.type]}40`,
                    fontFamily: 'Space Mono, monospace',
                  }}>
                    {event.type}
                  </span>
                  <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-full" style={{ 
                    color: ELEMENT_COLORS[event.element],
                    background: `${ELEMENT_COLORS[event.element]}20`,
                    border: `1px solid ${ELEMENT_COLORS[event.element]}40`,
                    fontFamily: 'Space Mono, monospace',
                  }}>
                    {event.element}
                  </span>
                </div>
                <h3 className="font-bold text-lg mb-2" style={{ color: '#EAF6FF', fontFamily: 'Rajdhani, system-ui' }}>
                  {event.title}
                </h3>
                <p className="text-xs mb-3" style={{ color: '#5A5A7A', fontFamily: 'Space Mono, monospace' }}>
                  {event.date}
                </p>
                <p className="text-sm leading-relaxed" style={{ color: '#8A8AA5', fontFamily: 'Inter, sans-serif' }}>
                  {event.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Season News */}
      <section className="py-20 px-4" style={{ background: 'rgba(13,13,21,0.5)' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="font-black text-4xl md:text-5xl uppercase text-center mb-4" style={{ 
            color: '#EAF6FF',
            fontFamily: 'Rajdhani, system-ui',
          }}>
            SEASON NEWS
          </h2>
          <p className="text-center mb-12" style={{ color: '#5A5A7A', fontFamily: 'Inter, sans-serif' }}>
            Latest updates and announcements
          </p>

          <div className="space-y-4 max-w-4xl mx-auto">
            {SEASON_NEWS.map((news) => (
              <div key={news.id} className="p-6 rounded-xl border transition-all hover:scale-[1.02]" style={{ 
                background: 'rgba(13,13,21,0.8)',
                borderColor: 'rgba(58,58,90,0.5)',
                backdropFilter: 'blur(8px)',
              }}>
                <div className="flex items-start justify-between mb-3">
                  <span className="text-xs" style={{ color: '#5A5A7A', fontFamily: 'Space Mono, monospace' }}>
                    {news.date}
                  </span>
                  <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-full" style={{ 
                    color: news.tag === 'announcement' ? '#FFE800' : news.tag === 'update' ? '#00F0FF' : '#39FF14',
                    background: news.tag === 'announcement' ? 'rgba(255,232,0,0.1)' : news.tag === 'update' ? 'rgba(0,240,255,0.1)' : 'rgba(57,255,20,0.1)',
                    border: `1px solid ${news.tag === 'announcement' ? 'rgba(255,232,0,0.3)' : news.tag === 'update' ? 'rgba(0,240,255,0.3)' : 'rgba(57,255,20,0.3)'}`,
                    fontFamily: 'Space Mono, monospace',
                  }}>
                    {news.tag}
                  </span>
                </div>
                <h3 className="font-bold text-lg mb-2" style={{ color: '#EAF6FF', fontFamily: 'Rajdhani, system-ui' }}>
                  {news.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: '#8A8AA5', fontFamily: 'Inter, sans-serif' }}>
                  {news.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t" style={{ borderColor: 'rgba(58,58,90,0.3)' }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded" style={{ 
              background: 'linear-gradient(135deg, #00F0FF, #BF00FF)',
            }} />
            <span className="font-bold text-sm" style={{ color: '#8A8AA5', fontFamily: 'Rajdhani, system-ui' }}>
              Atlas Beasts
            </span>
          </div>
          <p className="text-xs" style={{ color: '#5A5A7A', fontFamily: 'Space Mono, monospace' }}>
            © 2026 Atlas Beasts. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  )
}
